import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { insuranceAPI, vehicleAPI } from '../../api/index';
import { queryClient } from '../../App';
import { Insurance, Vehicle } from '../../types/index';
import { Button } from '../Button';

interface AddInsuranceModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  insurance?: Insurance
  onSuccess?: () => void
}

type PolicyType = 'third_party' | 'comprehensive' | 'own_damage'

const policyTypes: PolicyType[] = [
  'third_party',
  'comprehensive',
  'own_damage',
]

export default function AddInsuranceModal({
  open,
  onClose,
  customerId,
  insurance,
  onSuccess,
}: AddInsuranceModalProps) {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    policy_number: '',
    insurance_company: '',
    policy_type: '' as PolicyType | '',
    coverage_amount: '',
    premium_amount: '',
    start_date: '',
    expiry_date: '',
    agent_name: '',
    agent_phone: '',
    nominee_name: '',
    nominee_relation: '',
  })

  // Fetch vehicles for the customer
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useQuery({
    queryKey: ['vehicles', customerId],
    queryFn: () => vehicleAPI.listForCustomer(customerId),
    enabled: open,
  })

  useEffect(() => {
    if (insurance) {
      setFormData({
        vehicle_id: insurance.vehicle_id || '',
        policy_number: insurance.policy_number || '',
        insurance_company: insurance.insurance_company || '',
        policy_type: insurance.policy_type || '',
        coverage_amount: insurance.coverage_amount || '',
        premium_amount: insurance.premium_amount || '',
        start_date: insurance.start_date || '',
        expiry_date: insurance.expiry_date || '',
        agent_name: insurance.agent_name || '',
        agent_phone: insurance.agent_phone || '',
        nominee_name: insurance.nominee_name || '',
        nominee_relation: insurance.nominee_relation || '',
      })
    } else {
      setFormData({
        vehicle_id: '',
        policy_number: '',
        insurance_company: '',
        policy_type: '',
        coverage_amount: '',
        premium_amount: '',
        start_date: '',
        expiry_date: '',
        agent_name: '',
        agent_phone: '',
        nominee_name: '',
        nominee_relation: '',
      })
    }
  }, [insurance, open])

  const createMutation = useMutation({
    mutationFn: () =>
      insuranceAPI.create(customerId, {
        ...formData,
        policy_type: formData.policy_type || null,
        coverage_amount: formData.coverage_amount || null,
        premium_amount: formData.premium_amount || null,
      }),
    onSuccess: () => {
      toast.success('Insurance created')
      queryClient.invalidateQueries({ queryKey: ['insurances', customerId] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to create insurance')
    },
  })

  const updateMutation = useMutation({
    mutationFn: () =>
      insuranceAPI.update(insurance!.id, {
        ...formData,
        policy_type: formData.policy_type || null,
        coverage_amount: formData.coverage_amount || null,
        premium_amount: formData.premium_amount || null,
      }),
    onSuccess: () => {
      toast.success('Insurance updated')
      queryClient.invalidateQueries({ queryKey: ['insurances', customerId] })
      queryClient.invalidateQueries({ queryKey: ['insurance', insurance!.id] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error ?? 'Failed to update insurance')
    },
  })

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    isLoadingVehicles
  const isEdit = !!insurance

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.vehicle_id.trim() || !formData.policy_number.trim()) {
      toast.error('Vehicle and policy number are required')
      return
    }
    if (isEdit) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  if (!open) return null

  const vehicles = vehiclesData?.data?.vehicles || []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Insurance' : 'Add Insurance'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle *
              </label>
              {isEdit && insurance?.vehicle_reg ? (
                <div className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-100 text-gray-700">
                  {insurance.vehicle_reg}
                </div>
              ) : (
                <select
                  required
                  disabled={isLoading}
                  value={formData.vehicle_id}
                  onChange={(e) =>
                    setFormData({ ...formData, vehicle_id: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                >
                  <option value="">Select vehicle</option>
                  {Array.isArray(vehicles) && vehicles.map((vehicle: Vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Policy Number *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.policy_number}
                onChange={(e) =>
                  setFormData({ ...formData, policy_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="POL-2024-001"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Insurance Company
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.insurance_company}
                onChange={(e) =>
                  setFormData({ ...formData, insurance_company: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="ICICI, HDFC, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Policy Type
              </label>
              <select
                disabled={isLoading}
                value={formData.policy_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    policy_type: e.target.value as PolicyType | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select policy type</option>
                {policyTypes.map((type) => (
                  <option key={type} value={type}>
                    {type === 'third_party'
                      ? 'Third Party'
                      : type === 'comprehensive'
                        ? 'Comprehensive'
                        : 'Own Damage'}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Coverage Amount
              </label>
              <input
                type="number"
                step="1"
                min="0"
                disabled={isLoading}
                value={formData.coverage_amount}
                onChange={(e) =>
                  setFormData({ ...formData, coverage_amount: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="500000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Premium Amount
              </label>
              <input
                type="number"
                step="1"
                min="0"
                disabled={isLoading}
                value={formData.premium_amount}
                onChange={(e) =>
                  setFormData({ ...formData, premium_amount: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="5,000"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.start_date}
                onChange={(e) =>
                  setFormData({ ...formData, start_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Expiry Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.expiry_date}
                onChange={(e) =>
                  setFormData({ ...formData, expiry_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
          </div>

          {/* Row 5 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Agent Name
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.agent_name}
                onChange={(e) =>
                  setFormData({ ...formData, agent_name: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Agent Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Agent Phone
              </label>
              <input
                type="tel"
                disabled={isLoading}
                value={formData.agent_phone}
                onChange={(e) =>
                  setFormData({ ...formData, agent_phone: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="+91 98765 43210"
              />
            </div>
          </div>

          {/* Row 6 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nominee Name
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.nominee_name}
                onChange={(e) =>
                  setFormData({ ...formData, nominee_name: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Nominee Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nominee Relation
              </label>
              <input
                type="text"
                disabled={isLoading}
                value={formData.nominee_relation}
                onChange={(e) =>
                  setFormData({ ...formData, nominee_relation: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Spouse, Child, etc."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              variant="primary"
            >
              {isEdit ? 'Update Insurance' : 'Add Insurance'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
